import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLanguage } from '../context/LanguageContext';

const CookieBannerContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${({ theme }) => theme.colors.dark};
  color: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  z-index: 1000;
  display: ${({ visible }) => (visible ? 'flex' : 'none')};
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  font-size: 0.9rem;
  border-top: 1px solid ${({ theme }) => theme.colors.accent};
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const CookieText = styled.p`
  margin: 0;
  padding-right: ${({ theme }) => theme.spacing.md};
  
  @media (max-width: 768px) {
    margin-bottom: ${({ theme }) => theme.spacing.sm};
    padding-right: 0;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-end;
  }
`;

const Button = styled.button`
  background-color: ${({ primary, theme }) => primary ? theme.colors.accent : 'transparent'};
  color: ${({ primary, theme }) => primary ? theme.colors.white : theme.colors.light};
  border: ${({ primary, theme }) => primary ? 'none' : `1px solid ${theme.colors.light}`};
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  cursor: pointer;
  border-radius: 4px;
  font-size: 0.85rem;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${({ primary, theme }) => primary ? theme.colors.accentDark : 'rgba(255, 255, 255, 0.1)'};
  }
`;

const CookieBanner = () => {
  const { t, language } = useLanguage();
  const [visible, setVisible] = useState(false);
  
  // Vérifier si l'utilisateur a déjà accepté les cookies et appliquer le choix
  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookieConsent');
    
    // Si l'utilisateur a refusé les cookies, désactiver Google Analytics
    if (cookieConsent === 'declined') {
      // Vérifier si la fonction gtag existe avant de l'appeler
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
          analytics_storage: 'denied'
        });
      }
    }
    
    // Si l'utilisateur n'a pas encore fait de choix, afficher la bannière
    if (!cookieConsent) {
      // Attendre un peu avant d'afficher la bannière pour ne pas perturber l'expérience utilisateur immédiatement
      const timer = setTimeout(() => {
        setVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);
  
  const acceptCookies = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    
    // Vérifier si la fonction gtag existe avant de l'appeler
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted'
      });
    }
    
    setVisible(false);
  };
  
  const declineCookies = () => {
    localStorage.setItem('cookieConsent', 'declined');
    
    // Vérifier si la fonction gtag existe avant de l'appeler
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied'
      });
    }
    
    setVisible(false);
  };
  
  return (
    <CookieBannerContainer visible={visible}>
      <CookieText>{t('cookieBanner.message')}</CookieText>
      <ButtonContainer>
        <Button onClick={declineCookies}>{t('cookieBanner.decline')}</Button>
        <Button primary onClick={acceptCookies}>{t('cookieBanner.accept')}</Button>
        <Button as="a" href={`/${language}/mentions-legales`}>{t('cookieBanner.moreInfo')}</Button>
      </ButtonContainer>
    </CookieBannerContainer>
  );
};

export default CookieBanner;
